import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { MaintenanceProvider, useMaintenance, setMaintenanceCallback } from './contexts/MaintenanceContext';
import { CONFIG } from './utils/config';
import PublicLayout from './layouts/PublicLayout';
import DashboardLayout from './layouts/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Pricing from './pages/Pricing';
import Product from './pages/Product';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import MyCodesPage from './pages/MyCodesPage';
import GS1EditorPage from './pages/GS1EditorPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ForgotPassword from './pages/ForgotPassword';
import PasswordReset from './pages/PasswordReset';
import VerifyEmail from './pages/VerifyEmail';
import AccountPage from './pages/AccountPage';
import Maintenance from './pages/Maintenance';
import VCardManager from './pages/VCardManager';

import Privacy from './pages/Privacy';
import Shop from './pages/Shop';
import Cart from './pages/Cart';
import Terms from './pages/Terms';
import Rgpd from './pages/Rgpd';
import ScrollToTop from './utils/scrollToTop';

function MaintenanceHandler({ children }) {
  const { activateMaintenance, isInMaintenance } = useMaintenance();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    setMaintenanceCallback(activateMaintenance);
    return () => setMaintenanceCallback(null);
  }, [activateMaintenance]);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${CONFIG.BASE_URL}/health`, {
          signal: AbortSignal.timeout(5000),
        });
        const data = await response.json();
        if (data.maintenance === true) {
          activateMaintenance(data.message || '');
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de maintenance:', error);
        activateMaintenance('Le serveur est actuellement indisponible. Veuillez réessayer plus tard.');
      } finally {
        setIsChecking(false);
      }
    };

    checkMaintenance();
  }, [activateMaintenance]);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (isInMaintenance) {
    return <Maintenance />;
  }

  return children;
}

function App() {
  return (
    <LanguageProvider>
    <ThemeProvider>
      <MaintenanceProvider>
        <CartProvider>
          <MaintenanceHandler>
            <Router>
              <ScrollToTop />
              <Routes>
                {/* Public routes with header + footer */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/product" element={<Product />} />
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/cart" element={<Cart />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/password-reset" element={<PasswordReset />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/cookies" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/rgpd" element={<Rgpd />} />
                </Route>

                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Dashboard />} />
                  <Route path="my-codes" element={<MyCodesPage />} />
	          <Route path="vcard" element={<VCardManager />} />
                  <Route path="gs1" element={<GS1EditorPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="shop" element={<Shop />} />
                  <Route path="cart" element={<Cart />} />
                  <Route path="account" element={<AccountPage />} />
                </Route>
              </Routes>
            </Router>
          </MaintenanceHandler>
        </CartProvider>
      </MaintenanceProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
